import { compact } from 'es-toolkit';
import { coerceCostSummary, coerceUsageSummary } from '../../../../lib/ai/costs';
import { toTrimmedNonEmptyString } from '../../../../lib/utils/type-guards';
import {
  sanitizeBoolean,
  sanitizeModel,
  sanitizeNumber,
  sanitizeSearchMode,
  sanitizeSummaryLength,
  sanitizeSummaryMode,
  sanitizeTranscriptSource,
} from './filters';
import { selectTokenText } from './scorers';
import type {
  CompletionExtractionResult,
  SummaryCompletionPayload,
  SummaryMetadata,
  SummaryStatusPayload,
  SummaryTokenPayload,
  TokenExtractionResult,
  TokenSelectionContext,
} from './types';

const WHITESPACE_PATTERN = /\s+/;

export function extractStatusMetadata(payload: SummaryStatusPayload, metadata: SummaryMetadata): SummaryMetadata {
  const next: SummaryMetadata = { ...metadata };
  const phase = typeof payload.phase === 'string' ? payload.phase : null;

  applySummaryLength(payload, next);
  applyModelMetadata(payload, next);
  applyImagePreferences(payload, next);
  applyModePreferences(payload, next);
  applyTokenAndTimingMetrics(payload, next);
  applyTranscriptMetadata(payload, next, phase);
  applyCharacterMetrics(payload, next, phase);
  applyAggregateCounts(payload, next);

  if (phase !== undefined) {
    next.videoPipelinePhase = phase;
  }

  if (payload.message !== undefined) {
    const message = toTrimmedNonEmptyString(payload.message);
    next.statusMessage = message ?? null;
  }

  if (phase === 'model-resolved' && payload.model !== undefined) {
    const resolvedModel = sanitizeModel(payload.model);
    next.model = resolvedModel ?? null;
    next.resolvedModel = resolvedModel ?? null;
  }
  return next;
}

function applySummaryLength(payload: SummaryStatusPayload, next: SummaryMetadata): void {
  const summaryLength = sanitizeSummaryLength(payload.summaryLength);
  if (summaryLength) next.summaryLength = summaryLength;
}

function applyModelMetadata(payload: SummaryStatusPayload, next: SummaryMetadata): void {
  if (payload.model !== undefined) {
    const model = sanitizeModel(payload.model);
    next.model = model ?? null;
    next.resolvedModel = model ?? null;
  }

  if (payload.requestedModel !== undefined) {
    const requestedModel = sanitizeModel(payload.requestedModel);
    next.requestedModel = requestedModel ?? null;
  }

  if (payload.resolvedModel !== undefined) {
    const resolvedModel = sanitizeModel(payload.resolvedModel);
    next.resolvedModel = resolvedModel ?? null;
  }
}

function applyImagePreferences(payload: SummaryStatusPayload, next: SummaryMetadata): void {
  const numberFields: Array<{
    key: keyof SummaryStatusPayload;
    assign: (value: number | null) => void;
  }> = [
    {
      key: 'tokenHint',
      assign: (value) => {
        next.tokenHint = value === null ? null : Math.max(0, Math.trunc(value));
      },
    },
    {
      key: 'availableImages',
      assign: (value) => {
        next.availableImages = value === null ? null : Math.max(0, Math.trunc(value));
      },
    },
    {
      key: 'includedImages',
      assign: (value) => {
        next.includedImages = value === null ? null : Math.max(0, Math.trunc(value));
      },
    },
  ];

  for (const field of numberFields) {
    const rawValue = payload[field.key];
    if (rawValue === undefined) continue;
    const sanitized = sanitizeNumber(rawValue);
    field.assign(sanitized);
  }

  if (payload.shouldIncludeImages !== undefined) {
    const shouldIncludeImages = sanitizeBoolean(payload.shouldIncludeImages);
    next.shouldIncludeImages = shouldIncludeImages === null ? null : shouldIncludeImages;
  }

  if (payload.customPrompt !== undefined) {
    next.customPrompt = toTrimmedNonEmptyString(payload.customPrompt);
  }
}

function applyModePreferences(payload: SummaryStatusPayload, next: SummaryMetadata): void {
  if (payload.mode !== undefined) {
    next.mode = sanitizeSummaryMode(payload.mode);
  }

  if (payload.searchMode !== undefined) {
    next.searchMode = sanitizeSearchMode(payload.searchMode);
  }

  if (payload.requestedSearchMode !== undefined) {
    next.requestedSearchMode = sanitizeSearchMode(payload.requestedSearchMode);
  }

  if (payload.finishReason !== undefined) {
    next.finishReason = toTrimmedNonEmptyString(payload.finishReason);
  }
}

function applyTokenAndTimingMetrics(payload: SummaryStatusPayload, next: SummaryMetadata): void {
  if (payload.totalTokens !== undefined) {
    const totalTokens = sanitizeNumber(payload.totalTokens);
    next.totalTokens = totalTokens === null ? null : Math.max(0, Math.trunc(totalTokens));
  }

  if (payload.responseTimeMs !== undefined) {
    const responseTime = sanitizeNumber(payload.responseTimeMs);
    next.responseTimeMs = responseTime === null ? null : Math.max(0, Math.trunc(responseTime));
  }

  const truncated = sanitizeBoolean(payload.truncated);
  if (truncated !== undefined) next.truncated = truncated;

  const contentTruncated = sanitizeBoolean(payload.contentTruncated);
  if (contentTruncated !== undefined) next.contentTruncated = contentTruncated;

  const contentBudget = sanitizeNumber(payload.contentBudgetCharacters);
  if (contentBudget !== undefined) next.contentBudgetCharacters = contentBudget;
}

function applyTranscriptMetadata(payload: SummaryStatusPayload, next: SummaryMetadata, phase: string | null): void {
  const transcriptSource = sanitizeTranscriptSource(payload.transcriptSource);
  if (transcriptSource) {
    next.transcriptSource = transcriptSource;
  } else if (payload.transcriptSource === null) {
    next.transcriptSource = null;
  }

  if (phase !== 'started') {
    return;
  }

  const transcriptCharacters = sanitizeNumber(payload.transcriptCharacters);
  if (transcriptCharacters === undefined) {
    next.transcriptCharacters = null;
    next.transcriptLines = null;
    next.transcriptAvailable = false;
    next.transcriptMessage = 'No transcript detected; falling back to on-page HTML.';
    next.transcriptSource = null;
    return;
  }

  next.transcriptCharacters = transcriptCharacters;
  next.transcriptLines = sanitizeNumber(payload.transcriptLines);
  next.transcriptAvailable = true;
  next.transcriptMessage = null;
  if (transcriptSource) next.transcriptSource = transcriptSource;
}

function applyCharacterMetrics(payload: SummaryStatusPayload, next: SummaryMetadata, phase: string | null): void {
  const characters = sanitizeNumber(payload.characters);
  if (characters !== undefined) {
    if (phase === 'started') {
      next.sourceCharacters = characters;
    } else {
      next.summaryCharacters = characters;
    }
  }

  const wordCount = sanitizeNumber(payload.wordCount);
  if (wordCount !== undefined) next.wordCount = wordCount;
}

function applyAggregateCounts(payload: SummaryStatusPayload, next: SummaryMetadata): void {
  if (payload.consideredTweets !== undefined) {
    const consideredTweets = sanitizeNumber(payload.consideredTweets);
    next.consideredTweets = consideredTweets === null ? null : Math.max(0, Math.trunc(consideredTweets));
  }

  if (payload.includedLinks !== undefined) {
    const includedLinks = sanitizeNumber(payload.includedLinks);
    next.includedLinks = includedLinks === null ? null : Math.max(0, Math.trunc(includedLinks));
  }
}

export function extractTokenUpdate(
  payload: SummaryTokenPayload,
  metadata: SummaryMetadata,
  context: TokenSelectionContext
): TokenExtractionResult {
  const selection = selectTokenText(payload, context);
  const next: SummaryMetadata = { ...metadata };

  const characters = sanitizeNumber(payload.characters);
  if (characters !== undefined) next.summaryCharacters = characters;

  const wordCount = sanitizeNumber(payload.wordCount);
  if (wordCount !== undefined) next.wordCount = wordCount;

  return { text: selection.text, metadata: next };
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Streaming payloads toggle many optional fields; guard clauses keep the updates explicit.
export function extractCompletionUpdate(
  payload: SummaryCompletionPayload,
  metadata: SummaryMetadata,
  previousText: string
): CompletionExtractionResult {
  const next: SummaryMetadata = { ...metadata };
  const text = typeof payload.text === 'string' && payload.text.length > 0 ? payload.text : previousText;

  const characters = sanitizeNumber(payload.characters);
  if (characters === undefined) {
    next.summaryCharacters = text.length > 0 ? text.length : null;
  } else {
    next.summaryCharacters = characters;
  }

  const wordCount = sanitizeNumber(payload.wordCount);
  if (wordCount !== undefined) next.wordCount = wordCount;
  else if (text.length > 0) next.wordCount = compact(text.split(WHITESPACE_PATTERN)).length;

  const truncated = sanitizeBoolean(payload.truncated);
  if (truncated !== undefined) next.truncated = truncated;

  const contentTruncated = sanitizeBoolean(payload.contentTruncated);
  if (contentTruncated !== undefined) next.contentTruncated = contentTruncated;

  const contentBudget = sanitizeNumber(payload.contentBudgetCharacters);
  if (contentBudget !== undefined) next.contentBudgetCharacters = contentBudget;

  if (payload.requestedModel !== undefined) {
    const requestedModel = sanitizeModel(payload.requestedModel);
    next.requestedModel = requestedModel ?? null;
  }

  if (payload.model !== undefined) {
    const resolvedModel = sanitizeModel(payload.model);
    next.model = resolvedModel ?? null;
    next.resolvedModel = resolvedModel ?? null;
  }

  if (payload.tokenHint !== undefined) {
    const tokenHint = sanitizeNumber(payload.tokenHint);
    next.tokenHint = tokenHint === null ? null : Math.max(0, Math.trunc(tokenHint));
  }

  if (payload.availableImages !== undefined) {
    const availableImages = sanitizeNumber(payload.availableImages);
    next.availableImages = availableImages === null ? null : Math.max(0, Math.trunc(availableImages));
  }

  if (payload.includedImages !== undefined) {
    const includedImages = sanitizeNumber(payload.includedImages);
    next.includedImages = includedImages === null ? null : Math.max(0, Math.trunc(includedImages));
    if (next.shouldIncludeImages === null) {
      if (includedImages === null) {
        // Leave the value as null; completion events will populate it if available later.
      } else {
        next.shouldIncludeImages = includedImages > 0;
      }
    }
  }

  if (payload.shouldIncludeImages !== undefined) {
    const shouldIncludeImages = sanitizeBoolean(payload.shouldIncludeImages);
    next.shouldIncludeImages = shouldIncludeImages === null ? null : shouldIncludeImages;
  }

  if (payload.customPrompt !== undefined) {
    const customPrompt = toTrimmedNonEmptyString(payload.customPrompt);
    next.customPrompt = customPrompt;
  }

  if (payload.mode !== undefined) {
    const mode = sanitizeSummaryMode(payload.mode);
    next.mode = mode;
  }

  if (payload.searchMode !== undefined) {
    const searchMode = sanitizeSearchMode(payload.searchMode);
    next.searchMode = searchMode;
  }

  if (payload.requestedSearchMode !== undefined) {
    const requestedSearchMode = sanitizeSearchMode(payload.requestedSearchMode);
    next.requestedSearchMode = requestedSearchMode;
  }

  if (payload.finishReason !== undefined) {
    const finishReason = toTrimmedNonEmptyString(payload.finishReason);
    next.finishReason = finishReason;
  }

  if (payload.responseTimeMs !== undefined) {
    const responseTime = sanitizeNumber(payload.responseTimeMs);
    next.responseTimeMs = responseTime === null ? null : Math.max(0, Math.trunc(responseTime));
  }

  if (payload.consideredTweets !== undefined) {
    const consideredTweets = sanitizeNumber(payload.consideredTweets);
    next.consideredTweets = consideredTweets === null ? null : Math.max(0, Math.trunc(consideredTweets));
  }

  if (payload.includedLinks !== undefined) {
    const includedLinks = sanitizeNumber(payload.includedLinks);
    next.includedLinks = includedLinks === null ? null : Math.max(0, Math.trunc(includedLinks));
  }

  if (payload.usage !== undefined) next.usage = coerceUsageSummary(payload.usage);
  if (payload.cost !== undefined) next.cost = coerceCostSummary(payload.cost);

  const shouldSynthesizeUsage = next.usage === null || next.usage === undefined;
  if (shouldSynthesizeUsage && typeof payload.totalTokens === 'number' && Number.isFinite(payload.totalTokens)) {
    const totalTokens = Math.max(0, Math.trunc(payload.totalTokens));
    next.usage = { promptTokens: 0, completionTokens: 0, totalTokens };
  }

  next.statusMessage = null;
  next.videoPipelinePhase = null;

  return { text, metadata: next };
}
